/**
 * ONE place that decides whether Morpho's own `listed` curation applies.
 *
 * Morpho markets and Morpho prices are two queries against the same API,
 * filtered by the same `listed` predicate, issued from two different files —
 * `fetchPublic.ts` here and `prices/oracle-prices/fetchers/morpho.ts`. They
 * used to hold two independent booleans: the market side took a caller flag,
 * the price side hardcoded the filter. A market served without a price renders
 * a correct token amount next to `$0` with a blank LTV in the same row, because
 * `collateralFactor = lltvPrice / collateralPriceUsd` and the divisor is the
 * price nobody fetched.
 *
 * Both paths now resolve the decision HERE, per chain, so enabling a chain is
 * one edit and cannot be done by halves. `test/price/morphoListedParity.test.ts`
 * fails if either path stops asking.
 *
 * Per CHAIN rather than one global flag because that is the granularity the
 * decision actually has: `listed: false` is Morpho's curation, and unmuting it
 * everywhere would admit markets pinned at 100 % utilization publishing
 * unearnable rates (see `vaults/morpho/rateSanity.ts`). A caller may still pass
 * an explicit override — the resolver is the DEFAULT, not a ceiling.
 */
const MORPHO_UNLISTED_CHAINS = new Set<string>([
  // Robinhood Chain. 189 of its 194 markets are unlisted and essentially all of
  // them are Longbow — a curated Morpho Blue deployment (tokenized equities and
  // RWAs against USDG) that Morpho's own frontend does not carry, so `listed`
  // here says nothing about the market's quality. Enabled 2026-09-08 together
  // with the three things a chain needs before it can be:
  //   1. its collateral tokens in `token-lists` (wsNET was the last gap — the
  //      collateral of the chain's LARGEST market), or the rows price at $0,
  //   2. `lender-metadata`'s `SERVES_UNLISTED_CHAINS` carrying the same chain
  //      id, so the markets get a NAME and an oracle roster (5 -> 188 rows),
  //   3. a look at what the unlisted book actually holds — here 56 real
  //      markets, $524k supplied / $351k borrowed, no runaway rates (the
  //      highest borrow APR on the chain is 21.9 %).
  // See LONGBOW.md.
  '4663',
  // World Chain. blue-api indexes it, but 0 of its 30 markets are listed —
  // Morpho's frontend simply does not carry the chain, so `listed` says
  // nothing about the book (the Longbow shape again). Looked at 2026-09-16:
  // $59.5M supplied / $45.0M borrowed, of which the real two-sided markets are
  // USDC/WBTC $3.9M, USDC/WETH $3.6M, WETH/WBTC $2.1M, USDC/WLD $1.6M and
  // WETH/ezETH $1.1M at 0.1–4.6 % borrow APY; the $35.8M USDC/sdeUSD market is
  // pinned at 100 % utilization (297,994 % APY, $0 liquid) and is exactly what
  // `earn/rateSanity.ts` exists to drop. Token-list gaps on 480 at enablement:
  // sdeUSD and ezETH (collateral of two markets) — those rows price at $0 with
  // a blank LTV until token-lists carries them. `lender-metadata`'s
  // `SERVES_UNLISTED_CHAINS` was updated in the same change.
  '480',
])

/**
 * Whether unlisted Morpho markets are served (and priced) on this chain.
 * `false` means Morpho's `listed: true` filter is applied to both queries.
 */
export function morphoIncludesUnlisted(chainId: string): boolean {
  return MORPHO_UNLISTED_CHAINS.has(chainId)
}
